import trashIcon from '@adsk/bim360-matrix-react-assets/trash.svg';
import editIcon from '@adsk/bim360-matrix-react-assets/alloy-pencil.svg';
import browseIcon from '@adsk/bim360-matrix-react-assets/upload-icon.svg';
import ActionButton from '@adsk/bim360-matrix-react-action-button';
import Loader from '@adsk/bim360-matrix-react-loader';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import cx from 'classnames';
import lodashGet from 'lodash/get';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';
import {
  fetchDocumentDetailsIfMissing,
  navigateToIssueInDocumentAction,
  trackEvent,
  updateIssueAction,
  viewerPushpinThumbnailCreated
} from '../../app/actions';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {issueAttributesType} from '../../constants/proptype-validation';
import {linkIssueToDocument} from '../../utils/analytics-helpers';
import {constructLinkedDocument} from '../../utils/document-helpers';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import i18n from '../../utils/i18n';
import {getLinkedDocument} from '../../utils/issues-helpers';
import useCurrentIssuePreviewPopup, {IssuePreviewPopupTypes} from '../issues/useCurrentIssuePreviewPopup';
import LinkToDocumentContainer from './LinkToDocumentContainer';
import LinkToDocumentPlaceholder from './LinkToDocumentPlaceholder';
import {
  getCurrentViewType,
  getDocumentsFetchingVersions,
  getDocumentsVersions,
  getFetchingIssuesUrns,
  getLocationsTreeNodesDictionary,
  getRequiredFieldsFormatted
} from '../../app/selectors';
import {connect} from '../../app/connect';
import {getPushpinLocationId, hasPushpinLocationInfo} from '../../utils/pushpin-helpers';
import {locationChangeNotification} from '../../utils/notificationHelpers';

const DocumentDisplayName = ({
  issue,
  showHyperlink,
  document,
  fetchingIssuesUrns,
  documentsFetchingVersions,
  documentsVersions,
  viewType,
  dispatch,
  isRequired,
  allowOnlyUnlink,
  container,
  contentElementRef,
  locationsTreeNodesDictionary
}) => {
  const {urn, createdAtVersion} = getLinkedDocument(issue);
  const {setCurrentIssuePreviewPopup} = useCurrentIssuePreviewPopup();
  const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);
  const canEditLinkedDocument =
    featureFlags.get(FEATURE_FLAGS.LINK_UNLINK_DOCUMENT) &&
    issue.permittedAttributes.includes(ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS);
  const onSelectDocumentPushpin = useCallback(({document: newDocument, issuePushpin, currentSheet, thumbnail}) => {
    setIsUpdatingDocument(true);
    dispatch(fetchDocumentDetailsIfMissing(newDocument.urn));
    if (thumbnail) {
      dispatch(
        viewerPushpinThumbnailCreated({
          pushpinAttributes: issuePushpin,
          issueId: issue.id,
          thumbnail
        })
      );
    }
    const issueDocument = constructLinkedDocument({
      issueAttributes: issue,
      issuePushpin,
      document: newDocument,
      targetUrn: newDocument.urn,
      currentSheet
    });
    const pendingAttributes = {
      [ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS]: [issueDocument]
    };
    const updateLocation =
      featureFlags.get(FEATURE_FLAGS.UPDATE_LOCATION_WITH_PUSHPIN) && hasPushpinLocationInfo(issuePushpin);
    if (updateLocation) {
      pendingAttributes[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID] = getPushpinLocationId(issuePushpin);
    }
    dispatch(
      updateIssueAction(issue, pendingAttributes, {
        onSuccess: () => {
          dispatch(trackEvent(linkIssueToDocument(viewType)));
          setIsUpdatingDocument(false);
          updateLocation &&
            dispatch(
              locationChangeNotification(
                locationsTreeNodesDictionary[pendingAttributes[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]]?.name
              )
            );
        },
        onError: () => {
          setIsUpdatingDocument(false);
        },
        forceIssuesV2Api: true
      })
    );
  }, [dispatch, issue, locationsTreeNodesDictionary, viewType]);
  const actionBtnOptions = useMemo(() => {
    const options = [];
    if (!allowOnlyUnlink) {
      options.push(
        {
          key: 'edit-pushpin',
          label: i18n.t('issueAttributes.document.viewOrEdit'),
          svg: editIcon
        },
        {
          key: 'edit-document',
          label: i18n.t(
            isRequired ? 'issueAttributes.document.editRequiredField' : 'issueAttributes.document.changePushpin'
          ),
          svg: browseIcon
        }
      );
    }
    if (!isRequired) {
      options.push({
        key: 'unlink-document',
        label: i18n.t('issueAttributes.document.removeLinkedDocumentActionLabel'),
        svg: trashIcon
      });
    }
    return options;
  }, [allowOnlyUnlink, isRequired]);

  if (!urn) {
    return (
      <div className="DocumentDisplayName" data-testid="DocumentDisplayName">
        <div className="DocumentDisplayName__unspecified">
          {canEditLinkedDocument ? (
            <LinkToDocumentContainer
              container={container}
              status={issue[ISSUE_ATTRIBUTE_NAMES.STATUS]}
              onSelectDocumentPushpin={onSelectDocumentPushpin}
            >
              {startDocumentSelection => <LinkToDocumentPlaceholder startDocumentSelection={startDocumentSelection} />}
            </LinkToDocumentContainer>
          ) : (
            i18n.t('issueAttributes.document.unspecified')
          )}
        </div>
      </div>
    );
  }

  const isFetchingDocument = fetchingIssuesUrns.includes(urn) || documentsFetchingVersions.includes(urn);

  if (isFetchingDocument || isUpdatingDocument) {
    return (
      <div className="DocumentDisplayName" data-testid="DocumentDisplayName">
        <div className="DocumentDisplayName__loader">
          <span className="DocumentDisplayName__loader-text">
            {isUpdatingDocument ? i18n.t('navigation.updating') : i18n.t('navigation.loading')}
          </span>
          <Loader />
        </div>
      </div>
    );
  }

  const documentName = document?.name ?? document?.currentVersion?.title;

  if (!documentName) {
    return (
      <div className="DocumentDisplayName" data-testid="DocumentDisplayName">
        <div className="DocumentDisplayName__na">{i18n.t('issueAttributes.document.unknown')}</div>
      </div>
    );
  }

  const nameDivClassNames = cx('DocumentDisplayName__name', {
    'DocumentDisplayName__name--link': showHyperlink
  });

  const documentVersion = isNullOrUndefined(document.version) ? document.latestVersion : document.version;
  const issueStartingRevision = lodashGet(documentsVersions, [urn, createdAtVersion, 'revisionNumber']);
  const isCurrentVersion = issueStartingRevision === documentVersion;
  const currentVersionText = isCurrentVersion ? `, ${i18n.t('version.currentIndicator')}` : '';

  return (
    <div className="DocumentDisplayName" data-testid="DocumentDisplayName">
      <div className="DocumentDisplayName__name-wrapper">
        <span
          className={nameDivClassNames}
          data-testid="DocumentDisplayName__name"
          onClick={() => {
            if (showHyperlink) {
              dispatch(navigateToIssueInDocumentAction(document.urn, document.name, TYPE_QUALITY_ISSUES, issue.id));
            }
          }}
        >
          {documentName}
        </span>
        {documentVersion && (
          <span className="DocumentDisplayName__version">
            {`[${i18n.t('version.shorthand')}${issueStartingRevision}${currentVersionText}]`}
          </span>
        )}
        {canEditLinkedDocument && (
          <LinkToDocumentContainer
            container={container}
            status={issue[ISSUE_ATTRIBUTE_NAMES.STATUS]}
            className="DocumentDisplayName__edit-button"
            onSelectDocumentPushpin={onSelectDocumentPushpin}
          >
            {startDocumentSelection => (
              <ActionButton
                container={contentElementRef}
                options={actionBtnOptions}
                onSelect={key => {
                  if (key === 'edit-document') {
                    startDocumentSelection();
                  } else if (key === 'unlink-document') {
                    setCurrentIssuePreviewPopup(IssuePreviewPopupTypes.CONFIRM_UNLINK);
                  } else if (key === 'edit-pushpin') {
                    dispatch(
                      navigateToIssueInDocumentAction(document.urn, document.name, TYPE_QUALITY_ISSUES, issue.id)
                    );
                  }
                }}
              />
            )}
          </LinkToDocumentContainer>
        )}
      </div>
    </div>
  );
};

DocumentDisplayName.displayName = 'DocumentDisplayName';

DocumentDisplayName.propTypes = {
  issue: issueAttributesType.isRequired,
  fetchingIssuesUrns: PropTypes.arrayOf(PropTypes.string).isRequired,
  documentsFetchingVersions: PropTypes.arrayOf(PropTypes.string).isRequired,
  document: PropTypes.object,
  issueId: PropTypes.string,
  showHyperlink: PropTypes.bool,
  location: PropTypes.object,
  documentsVersions: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  allowOnlyUnlink: PropTypes.bool,
  container: PropTypes.object,
  contentElementRef: PropTypes.object,
  locationsTreeNodesDictionary: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  fetchingIssuesUrns: getFetchingIssuesUrns(state),
  documentsFetchingVersions: getDocumentsFetchingVersions(state),
  documentsVersions: getDocumentsVersions(state),
  viewType: getCurrentViewType(state),
  isRequired: getRequiredFieldsFormatted(state)[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS],
  locationsTreeNodesDictionary: getLocationsTreeNodesDictionary(state)
});

DocumentDisplayName.defaultProps = {
  allowOnlyUnlink: true
};

export default connect(mapStateToProps)(DocumentDisplayName);
